import {
  Badge,
  Divider,
  Flex,
  Heading,
  Image,
  List,
  ListItem,
  Stack,
  Text
} from '@chakra-ui/react'
import {InferGetServerSidePropsType} from 'next'
import {FC} from 'react'
import useSWR from 'swr'

import {ALink, ExternalLink} from 'lib/components/link'
import env from 'lib/config/public-env'
import {connectToDatabase} from 'lib/db/connect'
import {getJSON, ResponseError} from 'lib/utils/safe-fetch'

type Version = Record<string, string>

const API_URL = env.API_URL + '/version'
async function fetcher(url: string) {
  const res = await getJSON<Version>(url)
  if (!res.ok) throw res
  return res.data
}

async function dbIsConnected() {
  try {
    await connectToDatabase()
    return true
  } catch (e: unknown) {
    console.error(e)
    return e instanceof Error ? e : new Error(String(e))
  }
}

export async function getServerSideProps() {
  return {
    props: {
      dbIsConnected: await dbIsConnected()
    }
  }
}

const Status: FC<InferGetServerSidePropsType<typeof getServerSideProps>> = (
  p
) => {
  const {data, error, isValidating} = useSWR<Version, ResponseError>(
    API_URL,
    fetcher,
    {refreshInterval: 5_000}
  )
  const color = error ? 'red.500' : isValidating ? 'blue.500' : 'green.500'
  const text = error ? error.problem : data ? 'OK' : 'WAITING'

  return (
    <Flex justify='center' mt={40}>
      <Stack spacing={10} width='50rem'>
        <Flex justify='space-between'>
          <Heading size='2xl'>Status</Heading>
          <Image
            alt='Conveyal Logo'
            display='inline-block'
            boxSize={45}
            src='/logo.svg'
          />
        </Flex>
        <ALink to='regions'>← Back to the application</ALink>
        <Divider />
        <Flex justify='space-between'>
          <Heading size='xl'>
            Front-end
            <Badge colorScheme='blue' p={2} ml={2}>
              environment: {env.VERCEL_ENV}
            </Badge>
          </Heading>
          <Heading color='green.500' size='xl'>
            OK
          </Heading>
        </Flex>
        <Divider />
        <Flex justify='space-between'>
          <Heading size='xl'>Database</Heading>
          {p.dbIsConnected === true ? (
            <Heading color='green.500' size='xl'>
              OK
            </Heading>
          ) : (
            <Heading color='red.500' size='xl'>
              ERROR. {p.dbIsConnected === false ? '' : p.dbIsConnected.message}
            </Heading>
          )}
        </Flex>
        <Divider />
        <Stack spacing={5}>
          <Flex justify='space-between'>
            <Heading size='xl'>Server</Heading>
            <Heading color={color} size='xl'>
              {text}
            </Heading>
          </Flex>
          <ExternalLink href={API_URL}>{API_URL}</ExternalLink>
          {data ? (
            <List styleType='disc'>
              {Object.keys(data).map((k) => (
                <ListItem key={k}>
                  {k}:&nbsp;<strong>{data[k]}</strong>
                </ListItem>
              ))}
            </List>
          ) : (
            <Text>{error ? error.error.message : '...'}</Text>
          )}
        </Stack>
      </Stack>
    </Flex>
  )
}

export default Status
